import { createRoot } from "react-dom/client";
import { useEffect, useRef } from "react";
import BottomSheet from "@/components/shared/mobile/BottomSheet";
import { useBottomSheet } from "@/components/shared/hooks/mobile/useBottomSheet";

// React wrapper that renders the shared BottomSheet (components/shared/mobile/
// BottomSheet.tsx) around an existing server-rendered DOM node, so HAML/Stimulus
// pages reuse the SPA sheet behaviour (slide-up, drag-to-dismiss, scroll lock,
// pull-to-refresh) instead of duplicating it.
function MountedBottomSheet({ contentNode, isOpen, onClosed }) {
  const { showSheet, isAnimating, openSheet, closeSheet } = useBottomSheet();
  const containerRef = useRef(null);

  // Drive the sheet from the controller-owned `isOpen` flag.
  useEffect(() => {
    if (isOpen) openSheet();
    else closeSheet();
  }, [isOpen, openSheet, closeSheet]);

  // When the sheet finished closing (incl. backdrop tap / swipe), let the host
  // reset its `isOpen` so a later open() re-triggers the effect above.
  useEffect(() => {
    if (!showSheet) onClosed();
  }, [showSheet, onClosed]);

  // Move the server-rendered content into the sheet's scroll area.
  useEffect(() => {
    const container = containerRef.current;
    if (showSheet && container && contentNode) container.append(contentNode);
  }, [showSheet, contentNode]);

  return (
    <BottomSheet
      showSheet={showSheet}
      isAnimating={isAnimating}
      onClose={closeSheet}
    >
      <div ref={containerRef} />
    </BottomSheet>
  );
}

// Imperative adapter mirroring the previous vanilla helper's API
// (open(node) / close() / remove()) so Stimulus controllers stay
// framework-agnostic while the sheet itself is the shared React component.
export default class {
  constructor() {
    this.host = document.createElement("div");
    document.body.append(this.host);
    this.root = createRoot(this.host);
    this.contentNode = null;
    this.isOpen = false;
    this.handleClosed = () => {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.render();
    };
    // Mount once in the closed state so the initial "closed" effect runs while
    // isOpen is false (guarded no-op); a later open() then only flips isOpen
    // instead of mounting with isOpen=true and racing itself shut.
    this.render();
  }

  open(contentNode) {
    this.contentNode = contentNode;
    this.isOpen = true;
    this.render();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.render();
  }

  remove() {
    const { root, host } = this;
    // Defer so we never unmount synchronously from within a React callback.
    setTimeout(() => {
      root.unmount();
      host.remove();
    }, 0);
  }

  render() {
    this.root.render(
      <MountedBottomSheet
        contentNode={this.contentNode}
        isOpen={this.isOpen}
        onClosed={this.handleClosed}
      />,
    );
  }
}
